import InstantInput from "@/components/form/InstantInput"
import type { IncidentLog } from "@/models"
import { useCoreData, useUser } from "@/stores/coreDataStore"
import { formatEnum } from "@/utils/formatEnum"
import { css } from "vite-css-in-js"
import { computed } from "vue"
import { defineComponent, inferProps, proxyProp, type ReactiveComponent } from "vue-utils"

interface Props {
	incident: IncidentLog
}

const informationStyles = css`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 16rem), 1fr));
	gap: 1rem;

	@media print {
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 10rem), 1fr));
	}
`

const MainDetails: ReactiveComponent<Props> = (props) => {
	const coreData = useCoreData()

	const incident = proxyProp(props, "incident")
	const createdBy = useUser(() => incident.createdBy.userId)
	const smcOnDuty = useUser(() => incident.SMCOnDutyId)
	const reportedIncidentType = computed(() =>
		coreData.incidentTypes.find((i) => i.id === incident.reportedIncidentTypeId)
	)
	const finalIncidentType = computed(() => coreData.incidentTypes.find((i) => i.id === incident.finalIncidentTypeId))
	const primaryMWO = useUser(() => incident.primaryMWOId)
	const secondaryMWO = useUser(() => incident.secondaryMWOId)

	return () => (
		<div class="flex flex-col spacing-4">
			<div class={informationStyles}>
				<label>
					Incident Identifier
					<input type="text" value={incident.identifier} disabled />
				</label>
				<label>
					Incident Time (UTC)
					<InstantInput value={incident.incidentTime} setValue={() => void 0} disabled />
				</label>
				<label>
					Opened By
					<input type="text" value={createdBy.value?.fullName ?? "-"} disabled />
				</label>
				<label>
					Opened At (UTC)
					<InstantInput value={incident.createdBy.timeStamp} setValue={() => void 0} disabled />
				</label>
				<label>
					Status
					<input type="text" value={formatEnum(incident.status)} disabled />
				</label>
				<label>
					Reported Incident Type
					<input type="text" value={reportedIncidentType.value?.name ?? "-"} disabled />
				</label>
				<label>
					Final Incident Type
					<input type="text" value={finalIncidentType.value?.name ?? "N/A"} disabled />
				</label>
				<label>
					Initial Emergency Phase
					<input type="text" value={formatEnum(incident.initialEmergencyPhase)} disabled />
				</label>
				<label>
					Final Emergency Phase
					<input
						type="text"
						value={incident.finalEmergencyPhase ? formatEnum(incident.finalEmergencyPhase) : "N/A"}
						disabled
					/>
				</label>
				<label>
					SMC On Duty
					<input type="text" value={smcOnDuty.value?.fullName ?? "N/A"} disabled />
				</label>
				<label>
					Primary MWO
					<input type="text" value={primaryMWO.value?.fullName ?? "N/A"} disabled />
				</label>
				<label>
					Secondary MWO
					<input type="text" value={secondaryMWO.value?.fullName ?? "N/A"} disabled />
				</label>
				<label>
					Persons At Risk
					<input type="text" value={String(incident.personsAtRisk)} disabled />
				</label>
				<label>
					Name of Vessel or Casualty
					<input type="text" value={incident.nameOfVesselOrCasualty} disabled />
				</label>
			</div>
			<div class="flex flex-col spacing-4">
				<label>
					Message Received
					<textarea value={incident.messageReceived} disabled />
				</label>
				<label>
					Nature of Incident
					<textarea value={incident.natureOfIncident} disabled />
				</label>
				<label>
					Description of Casualty (if applicable)
					<textarea value={incident.descriptionOfCasualty} disabled />
				</label>
			</div>
		</div>
	)
}

export default defineComponent(MainDetails, inferProps<Props>())
